import { Token } from "./token";
import { Tokeniser } from "./tokeniser";
import { TokenType } from "./token-type";
import { TokenGroup } from "./token-group";
import { TreeNode } from "./tree-node";
import { NodeType } from "./node-type";
import { BracketManager } from "./bracket-manager";
import { IAdvancedFormData } from "../../interfaces/advanced-form-data";

import * as searchfields from "./search-fields.json";

export class SearchTreeBuilder {

    public static buildTreeFromTokens(tokenGroup: TokenGroup): TreeNode {

        /*
        *   Developers Note:
        *   Good code is expressive, and self-describing.  This is not good code, so its full of comments instead
        *   Hopefully these are enough to help you try and understand it
        *
        *
        *   Important notes for any who're keen to fiddle with the code
        *   1. The order of the IF statments matters, especially multi-part operators e.g Within 10 of
        *
        */

        const rootNode = new TreeNode("", NodeType.Root, null, "");
        let currentNode = rootNode;
        let currentToken: Token;
        const bracketManager = new BracketManager();
        let prevType: NodeType;
        let invalid = tokenGroup.Error ? true : false;
        let currentField = "";
        const tokens = tokenGroup.Tokens;

        for (let index = 0; index < tokens.length; index++) {
            currentToken = tokens[index];
            if (invalid) { // If we've detected an irregularity eg a rogue bracket or quote we treat everything subsequently as just a word
                currentNode.addChild(new TreeNode(currentToken.value, NodeType.Word, currentNode, currentField, false, index));
            } else {

                // Some words should be treated as plain words if they are at the start of a query or immediately after a (
                // Good example of this is cases with alternate names e.g M'alister (or Donoghue) v stevenson
                if (index === 0 || prevType === NodeType.OpenBracket) {
                    currentToken.disableKeywordsAtStartOfQuery();
                }
                if (index === tokens.length - 1) {
                    currentToken.disableKeywordsAtEndOfQuery();
                }

                // Check if its a bracket
                if (currentToken.type === TokenType.OpenBracket || currentToken.type === TokenType.OpenSquareBracket) {
                    invalid = bracketManager.push(currentToken);
                    const bracketNode = new TreeNode(currentToken.value, NodeType.OpenBracket, currentNode, currentField, currentToken.isTransient, index);
                    currentNode.addChild(bracketNode);
                    currentNode = bracketNode; // Everything inside brackets becomes a child of the opening bracket node
                    prevType = NodeType.OpenBracket;
                }
                if (currentToken.type === TokenType.CloseBracket || currentToken.type === TokenType.CloseSquareBracket) {
                    invalid = bracketManager.push(currentToken);
                    const bracketNode = new TreeNode(currentToken.value, NodeType.CloseBracket, currentNode, currentField, currentToken.isTransient, index);

                    // Some special cases to consider
                    if (invalid) { // need to check this here otherwise we can get null reference if this is the first close bracket in a string
                        currentNode.addChild(new TreeNode(currentToken.value, NodeType.Word, currentNode, currentField, false, index));
                    } else {
                        currentNode.addChild(bracketNode);
                        currentNode = currentNode.parent; // Now brackets are closed we step back up, the rest of the stuff should not be a child of the bracket node
                        if (currentNode.type === NodeType.FieldNameIncomplete) { // If we were closing the [] on a field name, we need to step up again so we aren't a child of the fieldName.
                            currentNode.type = NodeType.FieldNameComplete; // Mark this field as complete as its got a corresponding ] for its [
                            currentNode = currentNode.parent;
                            currentField = "";
                        }
                        if ((currentNode.type === NodeType.And || currentNode.type === NodeType.Or) && currentNode.children.length === 2) { // important special case for when a bracket follows an And or OR
                            currentNode = currentNode.parent; // If current node is an AND/OR and already has 2 children we need to step up another level
                        }
                        prevType = NodeType.CloseBracket;
                    }
                }

                // Bunch of simple stuff:
                if (currentToken.type === TokenType.PotentialDateOperator) {
                    const invalidChars = /[<>=]/;  //  don't allow '<', '>' or '='  between numbers
                    if (invalidChars.test(currentToken.value)) {
                        if (currentNode.children.length > 0 && $.isNumeric(currentNode.children[0].text)) {
                            tokenGroup.Error = "Your query has syntax error";
                        }
                    }
                    const dateOpNode = new TreeNode(currentToken.value, NodeType.DateOperator, currentNode, currentField, currentToken.isTransient, index);
                    currentNode.addChild(dateOpNode);
                    prevType = NodeType.DateOperator;
                }
                if (currentToken.type === TokenType.DefiniteDate) {
                    const dateNode = new TreeNode(currentToken.value, NodeType.Date, currentNode, currentField, currentToken.isTransient, index); // Don't think we strictly need to know its a date node but can't hurt to tag it up
                    currentNode.addChild(dateNode);
                    prevType = NodeType.Date;
                }
                // Dates and date operators such as from/to/before/after are ignored outside of a year field
                if (currentToken.type === TokenType.Word || currentToken.type === TokenType.Number
                    || currentToken.type === TokenType.PotentialOf) {
                    currentNode.addChild(new TreeNode(currentToken.value, NodeType.Word, currentNode, currentField, currentToken.isTransient, index));
                    prevType = NodeType.Word;
                }
                if (currentToken.type === TokenType.Phrase) {
                    currentNode.addChild(new TreeNode(currentToken.value, NodeType.Phrase, currentNode, currentField, currentToken.isTransient, index));
                    prevType = NodeType.Phrase;
                }
                if (currentToken.type === TokenType.Or) {
                    const orNode = new TreeNode(currentToken.value, NodeType.Or, currentNode, currentField, currentToken.isTransient, index);
                    currentNode.subsumeLastChildAndNext(orNode);
                    prevType = NodeType.Or;
                }
                if (currentToken.type === TokenType.And) {
                    const andNode = new TreeNode(currentToken.value, NodeType.And, currentNode, currentField, currentToken.isTransient, index);
                    currentNode.subsumeLastChildAndNext(andNode);
                    prevType = NodeType.And;
                }
                if (currentToken.type === TokenType.Near) {
                    const nearNode = new TreeNode(currentToken.value, NodeType.Near, currentNode, currentField, currentToken.isTransient, index);
                    currentNode.subsumeLastChildAndNext(nearNode);
                    prevType = NodeType.Near;
                }
                if (currentToken.type === TokenType.Wildcard) {
                    const wildcardNode = new TreeNode(currentToken.value, NodeType.Wildcard, currentNode, currentField, currentToken.isTransient, index);
                    currentNode.addChild(wildcardNode);
                    prevType = NodeType.Wildcard;
                }
                if (currentToken.type === TokenType.Not) {
                    const notNode = new TreeNode(currentToken.value, NodeType.Not, currentNode, currentField, currentToken.isTransient, index);
                    currentNode.addChildAndSubsumeNext(notNode);
                    prevType = NodeType.Not;
                }
                // e.g Stack w/10 dowden
                if (currentToken.type === TokenType.WOperator) {
                    const nextToken = tokens[index + 1];
                    if (prevType && prevType === NodeType.Word && nextToken.type === TokenType.Word) { // if we're surrounded by words (or numbers), consider it an operator
                        const wopNode = new TreeNode(currentToken.value, NodeType.WOperator, currentNode, currentField, currentToken.isTransient, index);
                        currentNode.subsumeLastChildAndNext(wopNode);
                        prevType = NodeType.WOperator;
                    } else { // otherwise its just a word
                        currentNode.addChild(new TreeNode(currentToken.value, NodeType.Word, currentNode, currentField, currentToken.isTransient, index));
                        prevType = NodeType.Word;
                    }
                }
                // "within keyword" - This ones a bit minging as "stack within 10 dowden" and "stack within 10 of dowden" are bot valid
                if (currentToken.type === TokenType.Within) {
                    const nextToken = tokens[index + 1];
                    const secondNextToken = tokens[index + 2];
                    const thirdNextToken = tokens[index + 3];
                    let nodesToSubsume = 2;
                    let nextTokenAfterOperator = secondNextToken;
                    let isOfPresent = false;

                    if ( prevType === NodeType.Word && nextToken && nextToken.type === TokenType.Number) { // only valid if preceded by word and followed by number
                        if (secondNextToken && secondNextToken.type === TokenType.PotentialOf) { // "within 10" and "within 10 of" are both valid
                            nodesToSubsume = 3;
                            nextTokenAfterOperator = thirdNextToken; // Skip past the "of" token if its present
                            isOfPresent = true;
                        }
                        if (nextTokenAfterOperator && nextTokenAfterOperator.type === TokenType.Word) { // Check we're followed by a word
                            const withinNode = new TreeNode(currentToken.value, NodeType.Within, currentNode, currentField, currentToken.isTransient, index);
                            secondNextToken.type = isOfPresent ? TokenType.DefiniteOf : secondNextToken.type; // If the "of" is present mark it as a definiteOf;
                            currentNode.subsumeLastChildAndNext(withinNode, nodesToSubsume);
                            prevType = NodeType.Within;
                        } else {
                            currentNode.addChild(new TreeNode(currentToken.value, NodeType.Word, currentNode, currentField, currentToken.isTransient, index));
                            prevType = NodeType.Word;
                        }

                    } else {
                        currentNode.addChild(new TreeNode(currentToken.value, NodeType.Word, currentNode, currentField, currentToken.isTransient, index));
                        prevType = NodeType.Word;
                    }
                }
                if (currentToken.type === TokenType.DefiniteOf) {
                    const ofNode = new TreeNode(currentToken.value, NodeType.Of, currentNode, currentField, currentToken.isTransient, index);
                    currentNode.addChildAndSubsumeNext(ofNode);
                    prevType = NodeType.Of;
                }
                if (currentToken.type === TokenType.DefiniteFieldName) {
                    const fieldNode = new TreeNode(currentToken.value, NodeType.FieldNameIncomplete, currentNode, currentField, currentToken.isTransient, index);
                    currentNode.addChild(fieldNode);
                    currentNode = fieldNode;
                    prevType = NodeType.FieldNameIncomplete;
                    currentField = currentToken.value;
                }
            }
        }

        if (tokenGroup.Error) {
            rootNode.addError(tokenGroup.Error);
        }
        if (bracketManager.Errors) {
            rootNode.addError(bracketManager.Errors);
        }
        return rootNode;
    }

    public static buildTreeFromFields(fields: IAdvancedFormData): TreeNode {
        const query = this.transformFieldsToQuery(fields);
        return this.buildTreeFromQuery(query);
    }

    public static buildTreeFromQuery(query: string): TreeNode {
        const tokeniser = new Tokeniser();
        const tokenGroup = tokeniser.tokenise(query);
        return this.buildTreeFromTokens(tokenGroup);
    }

    public static buildTreeFromSuggestionData(data): TreeNode {
        let query = "";
        if (data.Title) { query = query + ` Title[${data.Title}] `; }
        if (data.Reference) { query = query + `Citation[${data.Reference}] `; }
        if (data.Text) { query = query + `FullText[${data.Text}] `; }
        if (data.Category) {  query = query + `Category[${data.Category}] `; }
        if (data.KeyPhrase) {  query = query + `Category[${data.KeyPhrase}] `; }
        if (data.CourtName) { query = query + `Court[${data.CourtName}] `; }
        if (data.Year) { query = query + `Year[${data.Year}] `; }
        return this.buildTreeFromQuery(query);
    }

    private static transformFieldsToQuery(data: IAdvancedFormData): string {
        let query = "";
        const fieldNames = searchfields;

        Object.keys(fieldNames).forEach((key) => {
            if (this.isPresent(data[key])) {
                const fieldValue = data[key];
                const fieldName = fieldNames[key];
                query += ` ${fieldName.trim()}[${fieldValue}] `;
            }
        });

        query = query + (data.Anywhere ? data.Anywhere : "");
        return query;
    }

    private static isPresent(query: string): boolean {
        return query && query.trim().length > 0;
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/search/helpers/search-tree/search-tree-builder.ts